import React, { useContext, useEffect } from 'react';
import { axiosClient } from '../../../../library/Service/axiosClient';
import { SmartContext } from '../../../../library/Core/SmartContext';
import { DispatchEvent, State } from '../../../../library/Core/SmartTypes';
import PageBuilder from '../../../../library/Builders/PageBuilder/PageBuilder';
import AddOutreachFormLayout from './AddOutreachFormLayout';
import { evaluateExpression, isFormValid } from '../../../../library/Core/SmartFunctions';
import OutreachLogPageConfig from './OutreachLogPageConfig.json';

const AddOutreachModal = ({ onSuccess, editLog }: { onSuccess: () => void; editLog?: any }) => {
    const { state, dispatch } = useContext(SmartContext);

    const handleSave = async (
        event: React.SyntheticEvent<HTMLButtonElement>,
        state: State,
        dispatch: (dispatchEvent: DispatchEvent) => void
    ) => {
        event.preventDefault();

        const CALLING_MODES = ['PERSONAL_PHONE_CALL', 'IVR_ACEFONE', 'ZOOM_CALL', 'OFFICE_PHONE_RUNO'];
        const isCallingMode = CALLING_MODES.includes(state.data.outreachLog?.mode);

        // Step 1: check formValidationErrors from user interactions (pattern/length/custom rules)
        const isValid = isFormValid(state, dispatch);

        let hasRequiredError = false;
        const sections: any[] = OutreachLogPageConfig.sectionRepository || [];

        sections.forEach((section: any) => {
            (section.controlGroup || []).forEach((control: any) => {
                if (!control.required) return;
                if (control.hideExpression && evaluateExpression(control.hideExpression, state.data)) return;
                if (control.readOnlyExpression && evaluateExpression(control.readOnlyExpression, state.data)) return;

                const dataKey = `${section.id}.${control.id}`;
                const val = (state.data as any)[section.id]?.[control.id];
                const missing = val === undefined || val === null || String(val).length === 0;

                if (missing) {
                    hasRequiredError = true;
                    const label = control.props?.errorLabel || control.props?.label || control.id;
                    dispatch({
                        type: 'CONTROL_VALUE_CHANGE',
                        payload: { dataKey, name: control.id, value: '', errorMessages: [`Please enter "${label}"`] },
                    });
                }
            });
        });

        if (!isValid || hasRequiredError) {
            dispatch({ type: 'SHOW_ERRORS' });
            return;
        }

        try {
            const model = state.internal.model;
            const instituteId =
                state?.data?.selectedInstitute?.instituteId ||
                state?.data?.userInstitutes?.[0]?.instituteId;

            const attachmentFile = Array.isArray(state.data.outreachLog?.attachmentFile)
                ? state.data.outreachLog.attachmentFile.join(',')
                : state.data.outreachLog?.attachmentFile;

            const response = await axiosClient().post(
                `${process.env.REACT_APP_COMMON_API}/outreach-log/add-update`,
                { ...state.data.outreachLog, attachmentFile, duration: isCallingMode ? (state.data.outreachLog?.duration ?? null) : null, studentId: model.id, instituteId }
            );

            if (response.data.success) {
                onSuccess();
                dispatch({ type: 'TOGGLE_MODAL_VISIBILITY', payload: { modalName: 'showAddOutreachModal' } });
            }
        } catch (error) {
            console.error('Error submitting outreach log:', error);
        }
    };

    const handlePurposeCategoryChange = ({ dispatch }: any) => {
        dispatch({
            type: 'CONTROL_VALUE_CHANGE',
            payload: { dataKey: 'outreachLog.purposeSubCategory', name: 'purposeSubCategory', value: '', errorMessages: [] },
        });
    };

    const handleModeChange = ({ value, dispatch }: any) => {
        const CALLING_MODES = ['PERSONAL_PHONE_CALL', 'IVR_ACEFONE', 'ZOOM_CALL', 'OFFICE_PHONE_RUNO'];
        if (!CALLING_MODES.includes(value)) {
            dispatch({
                type: 'CONTROL_VALUE_CHANGE',
                payload: { dataKey: 'outreachLog.duration', name: 'duration', value: '', errorMessages: [] },
            });
        }
    };

    useEffect(() => {
        dispatch({ type: 'ADD_ACTIONS', payload: { ...state.actions, SAVE: handleSave, purposeCategory: handlePurposeCategoryChange, mode: handleModeChange } });
    }, []);

    return (
        <SmartContext.Provider value={{ state, dispatch }}>
            <AddOutreachFormLayout>
                {!state.flags.isDataLoading && <PageBuilder />}
            </AddOutreachFormLayout>
        </SmartContext.Provider>
    );
};

export default AddOutreachModal;
